import React, { useId } from 'react'
import { cn } from '@/lib/cn'

export type ToggleSize = 'sm' | 'md' | 'lg'

export interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  label?: string
  /** Secondary line under the label. Only rendered when `label` is set. */
  description?: string
  id?: string
  disabled?: boolean
  size?: ToggleSize
  /**
   * Where the switch sits relative to its label. `'end'` pushes it to the far
   * right — the settings-row convention — while `'start'` keeps it inline.
   */
  align?: 'start' | 'end'
  /** Accessible name when the switch is rendered without a visible label. */
  'aria-label'?: string
  className?: string
}

interface SizeSpec {
  /** Track dimensions. */
  track: string
  /** Knob dimensions. */
  knob: string
  /** Knob travel when checked. */
  translate: string
  /** Track inset, matching the knob's resting offset. */
  inset: string
  text: string
}

const sizeSpecs: Record<ToggleSize, SizeSpec> = {
  sm: {
    track: 'h-5 w-9',
    knob: 'h-4 w-4',
    translate: 'translate-x-4',
    inset: 'left-0.5 top-0.5',
    text: 'text-[13px]',
  },
  md: {
    track: 'h-6 w-11',
    knob: 'h-5 w-5',
    translate: 'translate-x-5',
    inset: 'left-0.5 top-0.5',
    text: 'text-sm',
  },
  lg: {
    track: 'h-7 w-[3.25rem]',
    knob: 'h-6 w-6',
    translate: 'translate-x-6',
    inset: 'left-0.5 top-0.5',
    text: 'text-sm',
  },
}

/**
 * Switch toggle with optional label and description.
 *
 * The knob animates on `transform` alone so the motion stays interruptible and
 * composited; the track cross-fades its color underneath. Both settle together
 * on one ease-out curve.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  id: idProp,
  disabled = false,
  size = 'md',
  align = 'start',
  className,
  ...rest
}: ToggleProps): React.ReactElement {
  const generatedId = useId()
  const id = idProp ?? `toggle-${generatedId}`
  const descriptionId = description ? `${id}-description` : undefined
  const spec = sizeSpecs[size]

  return (
    <label
      htmlFor={id}
      className={cn(
        'group flex items-start gap-3',
        align === 'end' && label && 'w-full flex-row-reverse justify-between',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
    >
      <span className="relative inline-flex shrink-0 items-center">
        <input
          id={id}
          type="checkbox"
          role="switch"
          aria-checked={checked}
          aria-describedby={descriptionId}
          disabled={disabled}
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
          {...rest}
        />

        {/* Track */}
        <span
          aria-hidden
          className={cn(
            'relative rounded-full transition-[background-color,box-shadow] duration-200 ease-out',
            'shadow-[inset_0_1px_2px_rgb(15_23_42/0.12)]',
            // Focus ring lives on the track so it traces the visible control.
            'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/45 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-gray-900',
            checked
              ? 'bg-primary'
              : 'bg-gray-300 dark:bg-gray-600',
            !disabled && !checked && 'group-hover:bg-gray-400 dark:group-hover:bg-gray-500',
            !disabled && checked && 'group-hover:bg-primary/85',
            disabled && 'opacity-50',
            spec.track
          )}
        >
          {/* Knob */}
          <span
            className={cn(
              'absolute rounded-full bg-white',
              'shadow-[0_1px_2px_rgb(15_23_42/0.25),0_1px_1px_rgb(15_23_42/0.06)]',
              'transition-transform duration-200 ease-out',
              !disabled && 'group-active:scale-[0.92]',
              spec.knob,
              spec.inset,
              checked ? spec.translate : 'translate-x-0'
            )}
          />
        </span>
      </span>

      {label && (
        <span className={cn('min-w-0 flex-1', disabled && 'opacity-60')}>
          <span
            className={cn(
              'block font-medium leading-5 text-gray-900 dark:text-gray-100',
              spec.text
            )}
          >
            {label}
          </span>
          {description && (
            <span
              id={descriptionId}
              className="mt-0.5 block text-[13px] leading-5 text-gray-500 dark:text-gray-400"
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  )
}

Toggle.displayName = 'Toggle'
